import { ref, inject } from "vue";
import { CommercialApiContract } from "@/contracts/CommercialApiContract";
import { HttpOperationResult } from "@/contracts/OperationResult";
import { PolicyScope } from "@/contracts/PolicyService";
import { PolicyFormsTracker } from "@/models/PrintDocumentModel";
import * as bdModels from "@/models/BulkDeductibleViewModels";
import * as ratingModels from "@/models/RatingModels";
import * as policyModels from "@/models/PolicyModels";
import * as configModels from "@/models/ConfigModels";
import * as batchModels from "@/models/BatchSummaryModels";
import { useFetch } from "@/composables/UseFetch";
import { DriverDetailRequestBody, ViolationOrderApiResponse } from "@/models/DriverInformationModels";
import { PolicyDocInfo } from "@/models/PolicyDocumentsModels";
import { get } from "http";

export function useCommercialApi(
	apiConfig: CommercialApiContract,
	fetchUtils: ReturnType<typeof useFetch> = useFetch(inject('auth')) // default to useFetch with auth service
) {
	const loading = ref(false);

	// this is used when Print Documents loads to request / start the document generation process
	async function docsDocGenRequest<TPayload, TResult>(messageRequest: TPayload, callback?: (result: HttpOperationResult<TResult>) => void): Promise<HttpOperationResult<TResult>> {
		loading.value = true;
		// Attempt to extract a correlationId from a standard envelope shape if present
		interface MaybeEnvelope { properties?: { correlationId?: string } }
		const correlationId: string | undefined = (messageRequest as unknown as MaybeEnvelope)?.properties?.correlationId;
		return fetchUtils.toOperationResult<TResult>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/messaging/topic`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				// custom property for UseFetch to pick up and inject X-Correlation-ID header
				...(correlationId ? { correlationId } : {}),
				body: JSON.stringify(messageRequest),
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	//this will be used to fetch the InteractiveForms collection so we can step through them
	async function getPolicyForms<T>(
		documentId: string,
		correlationId?: string,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/documents/${documentId}/interactive?${correlationId ? `correlationId=${correlationId}` : ''}`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
				...(correlationId ? { correlationId } : {}),
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	async function getCompletedPolicyForms<T>(
		documentId: string,
		correlationId?: string,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/documents/${documentId}/completed`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
				...(correlationId ? { correlationId } : {}),
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	//this is used between steps / during submission of data capture 
	async function putInteractiveForms<T>(
		policyFormsTracker: PolicyFormsTracker,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/documents/${policyFormsTracker.id}/interactive`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				...(policyFormsTracker.correlationId ? { correlationId: policyFormsTracker.correlationId } : {}),
				body: JSON.stringify(policyFormsTracker),
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	//this is used to edit completed interactive forms
	async function editCompletedInteractiveForms<T>(
		policyFormsTracker: PolicyFormsTracker,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/documents/${policyFormsTracker.id}/edit`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				...(policyFormsTracker.correlationId ? { correlationId: policyFormsTracker.correlationId } : {}),
				body: JSON.stringify(policyFormsTracker),
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	async function getBulkDeductibles(policyNumber: string): Promise<bdModels.BulkDeductibleViewModel> {
		return await fetch(`${apiConfig.endpoint}/policy/bulkdeductibles?policyNumber=${policyNumber}`)
			.then(response => response.json());
	}

	async function updateBulkDeductibles(post: bdModels.UpdateBulkDeductiblePost): Promise<Response> {
		const response = await fetch(`${apiConfig.endpoint}/policy/updatedeductibles`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;'
			},
			body: JSON.stringify(post),
		});

		if (response.ok) {
			return response;
		} else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function ratePolicy(reateRequest: ratingModels.RatePolicyRequest): Promise<Response> {
		const response = await fetch(`${apiConfig.endpoint}/rating/rate/policy`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;'
			},
			body: JSON.stringify(reateRequest)
		});

		if (response.ok) {
			return response;
		} else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getPolicyDocs(policyNumber: string, filter?: string): Promise<PolicyDocInfo[]> {
		let url = `${apiConfig.endpoint}/policy/${policyNumber}/docs`;

		if (filter) {
			url += `?filter=${encodeURIComponent(filter)}`;
		}

		const response = await fetchUtils.fetchResilientWithAuth(url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		if (response.ok) {
			const result: PolicyDocInfo[] = await response.json();
			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function searchPolicies(searchRequest: policyModels.PolicySearchRequest): Promise<policyModels.PolicySearchResponse> {
		const response = await fetch(`${apiConfig.endpoint}/policy/search`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(searchRequest)
		});

		if (response.ok) {
			const result: policyModels.PolicySearchResponse = await response.json();
			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getRatingDetails(documentId: string): Promise<ratingModels.RatePolicyResponse> {
		const response = await fetch(`${apiConfig.endpoint}/rating/ratingDetails/${documentId}`, {
			method: 'GET'
		});

		if (response.ok) {
			const result: ratingModels.RatePolicyResponse = await response.json();
			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getLossRatio(policyNumber: string): Promise<policyModels.LossRatioResponse> {
		const response = await fetch(`${apiConfig.endpoint}/workbench/policyloss/${policyNumber}`);

		if (response.ok) {
			const result: policyModels.LossRatioResponse = await response.json();

			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getMessageClientConnection(hubName: string): Promise<configModels.GetMessageClientConnectionInfoResponse> {
		const response = await fetchUtils.fetchWithAuth(`${apiConfig.endpoint}/config/for/messaging/${hubName}`, {
			method: 'POST'
		});

		if (response.ok) {
			const result: configModels.MessageClientConnectionInfo = await response.json();

			return new configModels.GetMessageClientConnectionInfoResponse({
				connectionInfo: result,
				success: true
			});
		}
		else {
			return new configModels.GetMessageClientConnectionInfoResponse({
				connectionInfo: null,
				message: `Error: ${response.status} - ${response.statusText}`,
				success: false
			});
		}
	}

	async function getPolicyJSON(policyNumber: string, scope: PolicyScope): Promise<string> {
		const response = await fetch(`${apiConfig.endpoint}/policy/${policyNumber}/${scope}`, {
			method: 'GET'
		});

		if (response.ok) {
			const result: string = await response.json();
			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getViolationPrediction(
		policyNumber: string,
		requestBody: DriverDetailRequestBody
	): Promise<ViolationOrderApiResponse> {
		const response = await fetch(
			`${apiConfig.endpoint}/drivers/policy/${policyNumber}/sync/license-report`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(requestBody)
			}
		);

		if (response.ok) {
			const result: ViolationOrderApiResponse = await response.json();
			return result;
		} else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getPolicy<T>(
		policyNumber: string,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/policy/${policyNumber}/Pending`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
		loading.value = false;
	});

	}

	async function getBatchSummaries(request: batchModels.GetBatchSummariesRequest): Promise<batchModels.BatchSummary[]> {
		const response = await fetch(`${apiConfig.endpoint}/getBatchSummaries`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(request)
		});

		if (response.ok) {
			const result: batchModels.BatchSummary[] = await response.json();
			return result;
		}
		else {
			throw new Error(`Error: ${response.status} - ${response.statusText}`);
		}
	}

	async function getDrivers<T>(
		policyNumber: string,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/policy/${policyNumber}/drivers`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});

	}

	async function getPolicyFormTrackers<T>(
		policyNumber: string,
		callback?: (result: HttpOperationResult<T>) => void
	): Promise<HttpOperationResult<T>> {
		loading.value = true;

		return fetchUtils.toOperationResult<T>(
			fetchUtils.fetchResilientWithAuth,
			`${apiConfig.endpoint}/policy/${policyNumber}/formTrackers`,
			{
				method: "GET",
				headers: { "Content-Type": "application/json" },
			},
			callback ? (result) => callback(result) : undefined
		).finally(() => {
			loading.value = false;
		});
	}

	return {
		docsDocGenRequest,
		getPolicyForms,
		getCompletedPolicyForms,
		putInteractiveForms,
		editCompletedInteractiveForms,
		getBulkDeductibles,
		updateBulkDeductibles,
		ratePolicy,
		searchPolicies,
		getRatingDetails,
		getLossRatio,
		getMessageClientConnection,
		getPolicyJSON,
		getViolationPrediction,
		getPolicy,
		getDrivers,
		getPolicyDocs,
		getBatchSummaries,
		getPolicyFormTrackers,
		loading,
	};
}